import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { completeOnboardingChecklist } from '../api/auth';
import styles from './FirstLoginChecklistPage.module.css';

const CHECKLIST_ITEMS = [
  'Confirm your access scope and the datasets you are allowed to view.',
  'Review the Smarti license and company-use boundaries.',
  'Open the dashboard selector and choose the workspace that matches your job.',
  'Ask an administrator for help if your plant, team, or project details are incomplete.',
];

export default function FirstLoginChecklistPage({ currentUser, onComplete }) {
  const { t } = useTranslation();
  const [checkedItems, setCheckedItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const canContinue = checkedItems.length === CHECKLIST_ITEMS.length && !submitting;

  const toggleItem = (index) => {
    setCheckedItems((current) => (
      current.includes(index)
        ? current.filter((item) => item !== index)
        : [...current, index]
    ));
  };

  const handleContinue = async () => {
    if (!canContinue) return;
    setSubmitting(true);
    setError('');
    try {
      const result = await completeOnboardingChecklist();
      onComplete?.(result.user || null);
    } catch (submitError) {
      setError(submitError.message || 'Could not complete onboarding.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className={styles.page}>
      <section className={styles.card}>
        <p className={styles.eyebrow}>{t('First login checklist')}</p>
        <h1 className={styles.title}>{t('Welcome to Smarti, {{name}}', { name: currentUser?.fullName || currentUser?.username || 'user' })}</h1>
        <p className={styles.subtitle}>
          {t('Before continuing, confirm the basics so Smarti starts cleanly for your role, site, and access level.')}
        </p>

        <div className={styles.metaGrid}>
          <div><span>{t('Company')}</span><strong>{currentUser?.company || '-'}</strong></div>
          <div><span>{t('Department')}</span><strong>{currentUser?.department || '-'}</strong></div>
          <div><span>{t('Plant')}</span><strong>{currentUser?.plant || '-'}</strong></div>
          <div><span>{t('Project')}</span><strong>{currentUser?.project || '-'}</strong></div>
        </div>

        <div className={styles.list}>
          {CHECKLIST_ITEMS.map((item, index) => (
            <label key={item} className={styles.item}>
              <input
                type="checkbox"
                checked={checkedItems.includes(index)}
                onChange={() => toggleItem(index)}
              />
              <span>{t(item)}</span>
            </label>
          ))}
        </div>

        {error ? <div className={styles.error}>{error}</div> : null}

        <button type="button" className={styles.button} disabled={!canContinue} onClick={handleContinue}>
          {submitting ? t('Saving...') : t('Finish setup and enter Smarti')}
        </button>
      </section>
    </main>
  );
}
